import { PaginatedDataAccessResult, PagingOptions } from "@acto/taskpane/core/data-access/types";
import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { Observation } from "@acto/taskpane/core/domain";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { ActiveObservationsFilter } from "@acto/gql/graphql";

export const ObservationUpdateWithClient = graphql(`
  fragment ObservationUpdate on Observation {
    id
    __typename
    status
    client {
      id
      __typename
    }
  }
`);

const GetObservationsQuery = graphql(`
  query GetObservations($first: Int!, $after: String, $filter: ActiveObservationsFilter) {
    activeObservations(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        cursor
        node {
          id
          __typename
          title {
            locale
            value
          }
          description {
            locale
            value
          }
          status
          processedReason
          client {
            ...ClientForDetailsBase
          }
          assignedEmployee {
            id
            __typename
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

export function useObservations({
  limit = 5,
  assignedEmployeeId,
  clientNamePrefixOrExternalReference,
  clientId,
  excludeDismissed,
  enabled = true,
}: PagingOptions & ActiveObservationsFilter & { enabled?: boolean }): PaginatedDataAccessResult<Observation> {
  const filter = useMemo(() => {
    if (clientNamePrefixOrExternalReference && clientNamePrefixOrExternalReference.length > 0) {
      return {
        assignedEmployeeId,
        clientId,
        clientNamePrefixOrExternalReference,
        excludeDismissed,
      };
    }

    return {
      assignedEmployeeId,
      clientId,
      excludeDismissed,
    };
  }, [clientNamePrefixOrExternalReference, assignedEmployeeId, clientId, excludeDismissed]);

  const { data, isPending, isFetching, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: GetObservationsQuery,
    limit,
    connection: (data) => data.activeObservations,
    variables: { filter },
    enabled,
  });

  const observations = useMemo(() => data?.map(Observation.fromGraphql), [data]);

  return {
    data: observations,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
